/**
 * SPH Screen-Space Fluid Rendering Pipeline
 *
 * Multi-pass rendering system:
 * 1. Depth pass - Render particle depth as sphere impostors
 * 2. Blur pass - Bilateral Gaussian blur for smooth depth field
 * 3. Normal pass - Reconstruct normals from smoothed depth
 * 4. Composite pass - Final fluid surface with refraction, fresnel, absorption
 */

// ============================================================================
// PASS 1: DEPTH RENDERING
// ============================================================================

export const FLUID_DEPTH_VERTEX = /* wgsl */`
struct VertexOutput {
  @builtin(position) position: vec4<f32>,
  @location(0) viewPos: vec3<f32>,
  @location(1) pointCoord: vec2<f32>,
  @location(2) particleRadius: f32,
}

struct RenderParams {
  viewMatrix: mat4x4<f32>,
  projMatrix: mat4x4<f32>,
  invProjMatrix: mat4x4<f32>,
  cameraPos: vec4<f32>,
  screenSize: vec2<f32>,
  particleScale: f32,
  _pad0: u32,
  _pad1: f32,
  _pad2: f32,
  _pad3: f32,
  _pad4: f32,
}

@group(0) @binding(0) var<uniform> renderParams: RenderParams;
@group(0) @binding(1) var<storage, read> positionMass: array<vec4<f32>>;

@vertex
fn main(
  @builtin(vertex_index) vertexIndex: u32,
  @builtin(instance_index) instanceIndex: u32
) -> VertexOutput {
  var output: VertexOutput;

  // Quad corners for billboard
  var corners = array<vec2<f32>, 4>(
    vec2<f32>(-1.0, -1.0),
    vec2<f32>(1.0, -1.0),
    vec2<f32>(-1.0, 1.0),
    vec2<f32>(1.0, 1.0)
  );
  let corner = corners[vertexIndex];
  output.pointCoord = corner * 0.5 + 0.5;

  // Get particle data
  let pos = positionMass[instanceIndex];
  let particlePos = pos.xyz;
  let particleMass = pos.w;

  // Particle radius
  let baseRadius = 0.05;
  output.particleRadius = baseRadius * renderParams.particleScale * sqrt(particleMass);

  // Transform to view space
  let viewPos = (renderParams.viewMatrix * vec4<f32>(particlePos, 1.0)).xyz;
  output.viewPos = viewPos;

  // Billboard quad
  let right = normalize(vec3<f32>(renderParams.viewMatrix[0][0], renderParams.viewMatrix[1][0], renderParams.viewMatrix[2][0]));
  let up = normalize(vec3<f32>(renderParams.viewMatrix[0][1], renderParams.viewMatrix[1][1], renderParams.viewMatrix[2][1]));

  let worldPos = particlePos + (right * corner.x + up * corner.y) * output.particleRadius;
  output.position = renderParams.projMatrix * vec4<f32>((renderParams.viewMatrix * vec4<f32>(worldPos, 1.0)).xyz, 1.0);

  return output;
}
`;

export const FLUID_DEPTH_FRAGMENT = /* wgsl */`
struct FragmentInput {
  @builtin(position) fragCoord: vec4<f32>,
  @location(0) viewPos: vec3<f32>,
  @location(1) pointCoord: vec2<f32>,
  @location(2) particleRadius: f32,
}

struct RenderParams {
  viewMatrix: mat4x4<f32>,
  projMatrix: mat4x4<f32>,
  invProjMatrix: mat4x4<f32>,
  cameraPos: vec4<f32>,
  screenSize: vec2<f32>,
  particleScale: f32,
  _pad0: u32,
  _pad1: f32,
  _pad2: f32,
  _pad3: f32,
  _pad4: f32,
}

@group(0) @binding(0) var<uniform> renderParams: RenderParams;

struct FragmentOutput {
  @builtin(frag_depth) depth: f32,
  @location(0) eyeSpaceDepth: f32,
}

@fragment
fn main(input: FragmentInput) -> FragmentOutput {
  var output: FragmentOutput;

  // Sphere impostor test
  let uv = input.pointCoord * 2.0 - 1.0;
  let dist2 = dot(uv, uv);

  if (dist2 > 1.0) {
    discard;
  }

  // Reconstruct sphere surface point in view space
  let z = sqrt(1.0 - dist2);
  let sphereNormal = vec3<f32>(uv.x, uv.y, z);

  // Offset view position by sphere surface
  let surfaceViewPos = input.viewPos + sphereNormal * input.particleRadius;

  // Project to clip space to get correct depth
  let clipPos = renderParams.projMatrix * vec4<f32>(surfaceViewPos, 1.0);
  output.depth = clipPos.z / clipPos.w;

  // Store eye-space depth for blur and normal reconstruction
  output.eyeSpaceDepth = -surfaceViewPos.z;

  return output;
}
`;

// ============================================================================
// PASS 2: BILATERAL BLUR (Compute Shader)
// ============================================================================

export const FLUID_BLUR_COMPUTE = /* wgsl */`
struct BlurParams {
  screenSize: vec2<u32>,
  kernelSize: u32, // e.g., 5 (radius)
  depthFalloff: f32, // Bilateral weight factor (e.g., 0.1)
  direction: vec2<f32>, // (1,0) for horizontal, (0,1) for vertical
  _pad0: f32,
  _pad1: f32,
}

@group(0) @binding(0) var<uniform> params: BlurParams;
@group(0) @binding(1) var inputDepth: texture_2d<f32>;
@group(0) @binding(2) var outputDepth: texture_storage_2d<r32float, write>;

// Gaussian weight
fn gaussian(x: f32, sigma: f32) -> f32 {
  return exp(-(x * x) / (2.0 * sigma * sigma));
}

@compute @workgroup_size(16, 16)
fn main(@builtin(global_invocation_id) globalId: vec3<u32>) {
  let coords = vec2<i32>(globalId.xy);
  let screenSize = vec2<i32>(params.screenSize);

  if (coords.x >= screenSize.x || coords.y >= screenSize.y) {
    return;
  }

  let centerDepth = textureLoad(inputDepth, coords, 0).r;

  // Skip background pixels
  if (centerDepth <= 0.0 || centerDepth >= 1000.0) {
    textureStore(outputDepth, coords, vec4<f32>(centerDepth, 0.0, 0.0, 0.0));
    return;
  }

  // Bilateral Gaussian blur
  var weightSum = 0.0;
  var depthSum = 0.0;

  let sigma = f32(params.kernelSize) / 3.0; // Gaussian sigma
  let kernelRadius = i32(params.kernelSize);

  for (var i = -kernelRadius; i <= kernelRadius; i++) {
    let offset = vec2<i32>(i32(params.direction.x * f32(i)), i32(params.direction.y * f32(i)));
    let sampleCoords = coords + offset;

    // Boundary check
    if (sampleCoords.x < 0 || sampleCoords.x >= screenSize.x ||
        sampleCoords.y < 0 || sampleCoords.y >= screenSize.y) {
      continue;
    }

    let sampleDepth = textureLoad(inputDepth, sampleCoords, 0).r;

    if (sampleDepth <= 0.0 || sampleDepth >= 1000.0) {
      continue;
    }

    // Spatial Gaussian weight
    let spatialWeight = gaussian(f32(i), sigma);

    // Bilateral depth weight (penalize depth discontinuities)
    let depthDiff = abs(sampleDepth - centerDepth);
    let depthWeight = gaussian(depthDiff, params.depthFalloff);

    let weight = spatialWeight * depthWeight;

    depthSum += sampleDepth * weight;
    weightSum += weight;
  }

  var blurredDepth = centerDepth;
  if (weightSum > 0.0) {
    blurredDepth = depthSum / weightSum;
  }

  textureStore(outputDepth, coords, vec4<f32>(blurredDepth, 0.0, 0.0, 0.0));
}
`;

// ============================================================================
// PASS 3: NORMAL RECONSTRUCTION (Compute Shader)
// ============================================================================

export const FLUID_NORMAL_COMPUTE = /* wgsl */`
struct NormalParams {
  screenSize: vec2<u32>,
  invProjMatrix: mat4x4<f32>,
  _pad0: vec2<f32>,
}

@group(0) @binding(0) var<uniform> params: NormalParams;
@group(0) @binding(1) var depthTexture: texture_2d<f32>;
@group(0) @binding(2) var normalTexture: texture_storage_2d<rgba16float, write>;

// Reconstruct view-space position from depth
fn viewPosFromDepth(uv: vec2<f32>, depth: f32) -> vec3<f32> {
  let clipPos = vec4<f32>(uv * 2.0 - 1.0, depth, 1.0);
  let viewPos = params.invProjMatrix * clipPos;
  return viewPos.xyz / viewPos.w;
}

@compute @workgroup_size(16, 16)
fn main(@builtin(global_invocation_id) globalId: vec3<u32>) {
  let coords = vec2<i32>(globalId.xy);
  let screenSize = vec2<i32>(params.screenSize);

  if (coords.x >= screenSize.x || coords.y >= screenSize.y) {
    return;
  }

  let centerDepth = textureLoad(depthTexture, coords, 0).r;

  // Skip background
  if (centerDepth <= 0.0 || centerDepth >= 1000.0) {
    textureStore(normalTexture, coords, vec4<f32>(0.0, 0.0, 1.0, 0.0));
    return;
  }

  // Sample neighboring depths
  let depthRight = textureLoad(depthTexture, coords + vec2<i32>(1, 0), 0).r;
  let depthLeft = textureLoad(depthTexture, coords + vec2<i32>(-1, 0), 0).r;
  let depthTop = textureLoad(depthTexture, coords + vec2<i32>(0, 1), 0).r;
  let depthBottom = textureLoad(depthTexture, coords + vec2<i32>(0, -1), 0).r;

  // Use center depth if neighbor is invalid
  let dRight = select(depthRight, centerDepth, depthRight <= 0.0 || depthRight >= 1000.0);
  let dLeft = select(depthLeft, centerDepth, depthLeft <= 0.0 || depthLeft >= 1000.0);
  let dTop = select(depthTop, centerDepth, depthTop <= 0.0 || depthTop >= 1000.0);
  let dBottom = select(depthBottom, centerDepth, depthBottom <= 0.0 || depthBottom >= 1000.0);

  // Compute view-space positions
  let uv = (vec2<f32>(coords) + 0.5) / vec2<f32>(screenSize);
  let uvRight = (vec2<f32>(coords + vec2<i32>(1, 0)) + 0.5) / vec2<f32>(screenSize);
  let uvLeft = (vec2<f32>(coords + vec2<i32>(-1, 0)) + 0.5) / vec2<f32>(screenSize);
  let uvTop = (vec2<f32>(coords + vec2<i32>(0, 1)) + 0.5) / vec2<f32>(screenSize);
  let uvBottom = (vec2<f32>(coords + vec2<i32>(0, -1)) + 0.5) / vec2<f32>(screenSize);

  let posCenter = viewPosFromDepth(uv, centerDepth);
  let posRight = viewPosFromDepth(uvRight, dRight);
  let posLeft = viewPosFromDepth(uvLeft, dLeft);
  let posTop = viewPosFromDepth(uvTop, dTop);
  let posBottom = viewPosFromDepth(uvBottom, dBottom);

  // Compute partial derivatives
  let ddx = posRight - posLeft;
  let ddy = posTop - posBottom;

  // Normal from cross product
  let normal = normalize(cross(ddx, ddy));

  textureStore(normalTexture, coords, vec4<f32>(normal, 1.0));
}
`;

// ============================================================================
// PASS 4: FLUID COMPOSITE (Fullscreen)
// ============================================================================

export const FLUID_COMPOSITE_VERTEX = /* wgsl */`
struct VertexOutput {
  @builtin(position) position: vec4<f32>,
  @location(0) uv: vec2<f32>,
}

// Fullscreen triangle (no vertex buffer needed)
@vertex
fn main(@builtin(vertex_index) vertexIndex: u32) -> VertexOutput {
  var output: VertexOutput;

  // Generate fullscreen triangle
  let x = f32((vertexIndex << 1u) & 2u);
  let y = f32(vertexIndex & 2u);

  output.position = vec4<f32>(x * 2.0 - 1.0, 1.0 - y * 2.0, 0.0, 1.0);
  output.uv = vec2<f32>(x, y);

  return output;
}
`;

export const FLUID_COMPOSITE_FRAGMENT = /* wgsl */`
struct FragmentInput {
  @location(0) uv: vec2<f32>,
}

struct CompositeParams {
  fluidColor: vec4<f32>, // Base fluid color (RGB) + opacity (A)
  absorptionCoeff: f32, // Beer's Law absorption coefficient
  refractionStrength: f32, // Refraction distortion strength
  fresnelF0: f32, // Fresnel reflectance at normal incidence
  specularPower: f32, // Phong specular exponent
  lightDir: vec3<f32>, // Light direction (view space)
  _pad0: f32,
  ambientLight: vec3<f32>, // Ambient light color
  _pad1: f32,
}

@group(0) @binding(0) var<uniform> params: CompositeParams;
@group(0) @binding(1) var depthTexture: texture_2d<f32>;
@group(0) @binding(2) var normalTexture: texture_2d<f32>;
@group(0) @binding(3) var backgroundTexture: texture_2d<f32>;
@group(0) @binding(4) var textureSampler: sampler;

// Schlick's Fresnel approximation
fn fresnel(cosTheta: f32, f0: f32) -> f32 {
  return f0 + (1.0 - f0) * pow(1.0 - cosTheta, 5.0);
}

@fragment
fn main(input: FragmentInput) -> @location(0) vec4<f32> {
  let depth = textureSample(depthTexture, textureSampler, input.uv).r;

  // Background pass-through
  if (depth <= 0.0 || depth >= 1000.0) {
    return textureSample(backgroundTexture, textureSampler, input.uv);
  }

  // Sample normal
  let normal = textureSample(normalTexture, textureSampler, input.uv).xyz;

  // Refraction: distort UV based on normal
  let refractedUV = input.uv + normal.xy * params.refractionStrength;
  let background = textureSample(backgroundTexture, textureSampler, clamp(refractedUV, vec2<f32>(0.0), vec2<f32>(1.0)));

  // View direction (pointing toward camera in view space)
  let viewDir = vec3<f32>(0.0, 0.0, 1.0);

  // Fresnel effect
  let cosTheta = abs(dot(normal, viewDir));
  let fresnelFactor = fresnel(cosTheta, params.fresnelF0);

  // Beer's Law absorption (approximated by depth/thickness)
  // Thickness estimated from depth - simple heuristic
  let thickness = depth * 0.1; // Scale factor
  let absorption = exp(-params.absorptionCoeff * thickness);
  var fluidColor = params.fluidColor.rgb * absorption;

  // Specular highlight (Blinn-Phong)
  let lightDir = normalize(params.lightDir);
  let halfDir = normalize(lightDir + viewDir);
  let specular = pow(max(dot(normal, halfDir), 0.0), params.specularPower);

  // Combine ambient + specular
  fluidColor += params.ambientLight * 0.2;
  fluidColor += vec3<f32>(1.0) * specular * 0.5;

  // Blend with background using fresnel
  let finalColor = mix(background.rgb * fluidColor, vec3<f32>(1.0), fresnelFactor * 0.3);

  return vec4<f32>(finalColor, params.fluidColor.a);
}
`;
